import type {
  AssetTimelineDateRange,
  AssetTimelineEventItem,
  AssetTimelineExtraItem,
  AssetTimelineExtraItemSlug,
  AssetTimelineFilters,
  AssetTimelineItem,
  AssetTimelineItemByType,
  GetTimelineItemsProps,
  GetTimelineStatusProps,
} from "./asset-timeline.type";
import type { AssetDetail } from "@/entities/asset";
import type { AssetEvent } from "@/entities/asset-event";
import type {
  ReferentialAssetEventSubtype,
  ReferentialAssetEventType,
} from "@/entities/referential/referential-asset-event-types";
import type { Nullable } from "@/types";
import type { Dayjs } from "dayjs";

import { t } from "i18next";

import { getAssetTheoricalRemainingLifespan } from "@/entities/asset";
import { getAssetEventTypeAndSubtype } from "@/entities/referential/referential-asset-event-types";
import { format, isInDateRange, sortDates } from "@/utilities/date";

import {
  ASSET_TIMELINE_DEFAULT_ITEM_COLOR,
  ASSET_TIMELINE_EXTRA_ITEMS,
  ASSET_TIMELINE_ITEM_ICONS,
} from "./asset-timeline.constant";

const getDatePickerValue = (date?: Dayjs | null) => {
  return date ? format(date) : null;
};

const getDatePickerValues = (dateRange?: AssetTimelineDateRange) => ({
  startDate: getDatePickerValue(dateRange?.[0]),
  endDate: getDatePickerValue(dateRange?.[1]),
});

export const convertDatePickerValuesToFetchFilterValue = (
  dateRange?: AssetTimelineDateRange
) => {
  const { startDate, endDate } = getDatePickerValues(dateRange);

  if (!startDate && !endDate) return null; // Return null to be able to reset filter previously defined

  return `${startDate},${endDate}`;
};

const isTimelineItemInFutur = (item: AssetTimelineItem): boolean => {
  return new Date(item.date).getTime() > new Date().getTime();
};

const isTimelineItemBeforeFutur = (
  item: AssetTimelineItem,
  nextItem: AssetTimelineItem
): boolean => {
  if (!nextItem) return false;

  const isInFutur = isTimelineItemInFutur(item);
  if (isInFutur) return false;

  return isTimelineItemInFutur(nextItem);
};

const isTimelineItemInDateRange = ({
  item,
  filters,
}: {
  item?: AssetTimelineExtraItem;
  filters: AssetTimelineFilters;
}): boolean => {
  const { startDate, endDate } = getDatePickerValues(filters.dateRange);

  if (!startDate || !endDate) return true;

  const options = { startDate, endDate, isInclusive: true };
  return isInDateRange(item?.date, options);
};

const buildTimelineExtraItem = (
  slug: AssetTimelineExtraItemSlug,
  date?: Nullable<string>
): AssetTimelineExtraItem | undefined => {
  if (!date) return undefined;

  const translationKey = `timeline.extraItems.${slug}`;
  const itemData = ASSET_TIMELINE_EXTRA_ITEMS[slug];

  // Make sure description is empty when no translation found (ex: Today)
  const descriptionKey = `${translationKey}.description`;
  const descriptionValue = t(`asset:${descriptionKey}`);
  const description =
    descriptionValue !== descriptionKey ? descriptionValue : undefined;

  return {
    ...itemData,
    key: `extra-item-${slug}`,
    title: t(`asset:${translationKey}.title`),
    description,
    date,
  };
};

const getManufacturedEvent = (asset?: AssetDetail) => {
  const date = asset?.date_manufacture;
  return buildTimelineExtraItem("manufactured", date);
};

const getCommissionedEvent = (asset?: AssetDetail) => {
  const date = asset?.date_service;
  return buildTimelineExtraItem("commissioned", date);
};

const getTheoreticalEndOfLifeEvent = (asset?: AssetDetail) => {
  const remainingLifespan = getAssetTheoricalRemainingLifespan(asset);

  if (!remainingLifespan) return undefined;

  return buildTimelineExtraItem(
    "theoretical-end-of-life",
    remainingLifespan.date
  );
};

const getTodayIndicator = () => {
  const date = format(new Date());
  return buildTimelineExtraItem("today", date);
};

const getTimelineExtraItems = ({
  asset,
  filters,
}: {
  asset?: AssetDetail;
  filters: AssetTimelineFilters;
}): AssetTimelineExtraItem[] => {
  if (!asset?.id) return [];

  const extraItems = [
    getTodayIndicator(),
    getManufacturedEvent(asset),
    getCommissionedEvent(asset),
    getTheoreticalEndOfLifeEvent(asset),
  ];

  return extraItems.filter(
    (item): item is AssetTimelineExtraItem =>
      item !== undefined && isTimelineItemInDateRange({ item, filters })
  );
};

const formatTimelineAssetEvent = (
  assetEvent: AssetEvent,
  assetEventTypes: ReferentialAssetEventType[]
): AssetTimelineEventItem => {
  const { type, subType } = getAssetEventTypeAndSubtype(
    assetEvent,
    assetEventTypes
  );

  return {
    type: "asset-event",
    key: `asset-event-${assetEvent.id}`,
    date: assetEvent.occurring_date_time,
    title: subType?.name ?? assetEvent.sub_type,
    description: subType?.description ?? "",
    color: subType?.color || ASSET_TIMELINE_DEFAULT_ITEM_COLOR,
    icon: getAssetTimelineEventSubTypeIcon(subType),
    data: assetEvent,
    eventType: type,
    eventSubType: subType,
  };
};

const getAssetTimelineEventSubTypeIcon = (
  subType?: ReferentialAssetEventSubtype
) => {
  return isValidAssetTimelineIconPath(subType?.icon)
    ? ASSET_TIMELINE_ITEM_ICONS[subType.icon]
    : undefined;
};

const isValidAssetTimelineIconPath = (
  iconPath?: string | null
): iconPath is keyof typeof ASSET_TIMELINE_ITEM_ICONS => {
  return iconPath ? iconPath in ASSET_TIMELINE_ITEM_ICONS : false;
};

export const getTimelineItems = ({
  asset,
  assetEvents,
  assetEventTypes,
  filters,
}: GetTimelineItemsProps) => {
  // Format asset-event items
  const assetEventItems = assetEvents.map((assetEvent) =>
    formatTimelineAssetEvent(assetEvent, assetEventTypes)
  );

  // Get extra items (aka items generated in front-end:
  // "custom-asset-event" & "custom-event" items)
  const extraItems = getTimelineExtraItems({ asset, filters });

  // Re-sort all items to make sure extra items are well
  // sorted with asset-event items
  const items: AssetTimelineItem[] = [...assetEventItems, ...extraItems].sort(
    (itemA, itemB) => sortDates(itemA.date, itemB.date)
  );

  // After re-sort, check isInFutur & isBeforeFutur
  return items.map((item, index): AssetTimelineItem => {
    const isInFutur = isTimelineItemInFutur(item);
    const nextItem = items[index + 1];
    const isBeforeFutur = isTimelineItemBeforeFutur(item, nextItem);

    return {
      ...item,
      isInFutur,
      isBeforeFutur,
    };
  });
};

export const getTimelineStatus = ({
  items,
  assetEventsQuery,
  assetQuery,
}: GetTimelineStatusProps) => {
  const isLoading = assetEventsQuery.isLoading || assetQuery.isLoading;
  const error = assetEventsQuery.error || assetQuery.error;
  const isSuccess = assetEventsQuery.isSuccess || assetQuery.isSuccess;

  // Group items by type
  const bucket: AssetTimelineItemByType = {};
  const itemsByType = items.reduce((groupedItems, item) => {
    const { type } = item;
    groupedItems[type] = groupedItems[type] ?? [];
    groupedItems[type].push(item);
    return groupedItems;
  }, bucket);

  const noAssetEvent = !itemsByType["asset-event"]?.length;
  const noCustomAssetEvent = !itemsByType["custom-asset-event"]?.length;

  // Show "Today" only if there is some "asset-event" and "custom-asset-event" items
  const isEmpty = isSuccess && noAssetEvent && noCustomAssetEvent;

  return {
    isLoading,
    error,
    isEmpty,
  };
};
